import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { SearchSelect } from "@/components/SearchSelect";

export const TIPI_APP = ["Tagliando", "Riparazione", "Consegna bici", "Ritiro bici", "Biomeccanica", "Altro"];
export const TIPI_APP_NOLEGGIO = ["Ritiro noleggio", "Riconsegna noleggio", "Prova bici", "Altro"];
const EMPTY = { area: "officina", cliente_id: "", nome_libero: "", telefono_libero: "", bici_id: "", bici_noleggio_id: "", data: "", ora: "09:00", durata_min: 60, tipo: "Tagliando", stato: "programmato", note: "" };
const L = ({ children }) => <Label className="mb-1.5 block text-xs font-bold uppercase tracking-wider text-slate-500">{children}</Label>;

export const AppuntamentoDialog = ({ open, onOpenChange, item, clienti, bici, flotta = [], onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [libero, setLibero] = useState(false);
  useEffect(() => {
    if (open) {
      setF({ ...EMPTY, ...Object.fromEntries(Object.keys(EMPTY).filter((k) => item?.[k] !== undefined && item?.[k] !== null).map((k) => [k, item[k]])) });
      setLibero(Boolean(item?.nome_libero) && !item?.cliente_id);
    }
  }, [open, item]);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const biciCliente = bici.filter((b) => b.cliente_id === f.cliente_id);
  const noleggio = f.area === "noleggio";
  const tipi = noleggio ? TIPI_APP_NOLEGGIO : TIPI_APP;
  const setArea = (area) => setF((s) => ({ ...s, area, tipo: (area === "noleggio" ? TIPI_APP_NOLEGGIO : TIPI_APP)[0] }));

  const submit = async (e) => {
    e.preventDefault();
    if (libero ? !f.nome_libero.trim() : !f.cliente_id) return toast.error(libero ? "Scrivi il nome del cliente" : "Seleziona un cliente");
    if (!f.data || !f.ora) return toast.error("Inserisci data e ora");
    setSaving(true);
    try {
      const payload = { ...f, durata_min: Number(f.durata_min) || 60, ...(libero ? { cliente_id: "", bici_id: "" } : { nome_libero: "", telefono_libero: "" }) };
      const { data } = item?.id ? await api.put(`/appuntamenti/${item.id}`, payload) : await api.post("/appuntamenti", payload);
      toast.success(item?.id ? "Appuntamento aggiornato" : "Appuntamento fissato");
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="appuntamento-dialog">
        <DialogHeader><DialogTitle className="font-heading text-xl">{item?.id ? "Modifica appuntamento" : "Nuovo appuntamento"}</DialogTitle><DialogDescription>Fissa data, ora e tipo di intervento.</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="sm:col-span-2"><L>Appuntamento per</L>
            <div className="grid grid-cols-2 gap-2">
              {[["officina", "Officina"], ["noleggio", "Noleggio"]].map(([v, l]) => (
                <button key={v} type="button" onClick={() => setArea(v)} data-testid={`app-area-${v}`}
                  className={`rounded-md border px-3 py-2.5 text-sm font-semibold transition-colors ${f.area === v ? "border-orange-600 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div className="sm:col-span-2">
            <div className="mb-1.5 flex items-center justify-between gap-2"><L>Cliente *</L>
              <button type="button" onClick={() => setLibero((v) => !v)} className="text-xs font-semibold text-orange-600 hover:underline" data-testid="app-cliente-libero-toggle">
                {libero ? "Scegli un cliente registrato" : "Cliente non registrato? Scrivi il nome"}
              </button>
            </div>
            {libero ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Input placeholder="Nome e cognome" value={f.nome_libero} onChange={(e) => set("nome_libero", e.target.value)} data-testid="app-nome-libero-input" />
                <Input type="tel" placeholder="Telefono (per WhatsApp)" value={f.telefono_libero} onChange={(e) => set("telefono_libero", e.target.value)} data-testid="app-telefono-libero-input" />
              </div>
            ) : (
              <SearchSelect testId="app-cliente-select" value={f.cliente_id} placeholder="Cerca cliente..." onChange={(v) => setF((s) => ({ ...s, cliente_id: v, bici_id: "" }))}
                options={clienti.map((c) => ({ value: c.id, label: `${c.nome} ${c.cognome || ""}`.trim(), hint: c.telefono }))} />
            )}
          </div>
          <div><L>{noleggio ? "Bici a noleggio" : "Bici"}</L>
            {noleggio ? (
              <Select value={f.bici_noleggio_id || undefined} onValueChange={(v) => set("bici_noleggio_id", v)} disabled={!flotta.length}>
                <SelectTrigger data-testid="app-bici-noleggio-select"><SelectValue placeholder={flotta.length ? "Seleziona bici della flotta..." : "Flotta vuota"} /></SelectTrigger>
                <SelectContent>{flotta.map((b) => <SelectItem key={b.id} value={b.id}>{b.codice ? `${b.codice} · ` : ""}{b.marca} {b.modello}</SelectItem>)}</SelectContent>
              </Select>
            ) : (
            <Select value={f.bici_id || undefined} onValueChange={(v) => set("bici_id", v)} disabled={libero || !biciCliente.length}>
              <SelectTrigger data-testid="app-bici-select"><SelectValue placeholder={biciCliente.length ? "Seleziona bici..." : "Nessuna bici"} /></SelectTrigger>
              <SelectContent>{biciCliente.map((b) => <SelectItem key={b.id} value={b.id}>{b.marca} {b.modello}</SelectItem>)}</SelectContent>
            </Select>
            )}
          </div>
          <div><L>Tipo</L>
            <Select value={f.tipo} onValueChange={(v) => set("tipo", v)}>
              <SelectTrigger data-testid="app-tipo-select"><SelectValue /></SelectTrigger>
              <SelectContent>{tipi.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><L>Data *</L><Input type="date" value={f.data} onChange={(e) => set("data", e.target.value)} data-testid="app-data-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><L>Ora *</L><Input type="time" step="900" value={f.ora} onChange={(e) => set("ora", e.target.value)} data-testid="app-ora-input" /></div>
            <div><L>Durata (min)</L><Input type="number" min="5" step="5" value={f.durata_min} onChange={(e) => set("durata_min", e.target.value)} data-testid="app-durata-input" /></div>
          </div>
          <div className="sm:col-span-2"><L>Note</L><Textarea rows={2} value={f.note} onChange={(e) => set("note", e.target.value)} data-testid="app-note-input" /></div>
          <DialogFooter className="sm:col-span-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} data-testid="app-cancel-button">Annulla</Button>
            <Button type="submit" disabled={saving} className="bg-orange-600 hover:bg-orange-700" data-testid="app-save-button">{saving ? "Salvataggio..." : "Salva"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
